import { useEffect, useRef } from 'react';

/**
 * useGTM — injects a Google Tag Manager container on public tenant pages.
 *
 * Isolation: The GTM container ID is fetched from the tenant's
 * `/api/public/*` metadata endpoint, so only the tenant's own container is
 * loaded on their own public pages. Admin/backoffice pages never load GTM.
 *
 * Injects both:
 *   1. The `<script>` loader into <head>
 *   2. The `<noscript>` iframe fallback into <body>
 *
 * Also seeds `window.dataLayer` so any custom events pushed by the app are
 * captured by tags configured inside GTM.
 */
export function useGTM(containerId) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!containerId) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (initializedRef.current) return undefined;
    // If a GTM container from a different tenant somehow was previously
    // loaded in this tab, we skip to preserve strict isolation.
    if (document.querySelector('script[data-meadd-gtm]')) return undefined;
    initializedRef.current = true;

    // Vanilla GTM loader (official snippet, formatted).
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    script.setAttribute('data-meadd-gtm', '1');
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }

    // <noscript> iframe fallback (recommended by Google — right after <body>).
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.setAttribute('data-meadd-gtm-noscript', '1');
    noscript.appendChild(iframe);
    if (document.body.firstChild) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      document.body.appendChild(noscript);
    }

    return undefined;
  }, [containerId]);

  return { ready: !!containerId && initializedRef.current };
}
